import React, { useState } from "react";
import { T } from "../theme";
import Pill, { STEP_TONE } from "./Pill";
import { CLIENT_PHASES, roadmapSlice, shortDate, weekdayDate } from "./now";
import Timeline from "./Timeline";
import { whenLines, zonesFor } from "../time";
import { sessionDots, chartStatus, upcomingSessions } from "./sessions";

// The roadmap, read-only and folded: the last thing done, then the next
// three. "Show all" opens every stored Activation / In Flight step by phase.
// Never falls back to the default steps (that stays in the editable view).
// A step row. The pill only prints when it says something (in progress,
// done): "To start" on every future row is non-data ink (design review 2).
// The date, or for a timed step one line per zone (client HQ, then NZ),
// each with its own day. From main's milestone-times feature (src/time.js).
const When = ({ r, zones }) => {
  const lines = whenLines(r, zones);
  if (lines.length === 0) return <span className="row-date" style={{ fontSize: 13, color: T.fgSecondary, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{weekdayDate(r.date)}</span>;
  return (
    <span className="row-date" style={{ display: "inline-flex", flexDirection: "column", gap: 2, textAlign: "right" }}>
      {lines.map((l, i) => <span key={i} style={{ fontSize: 13, whiteSpace: "nowrap", color: i === 0 ? T.fgSecondary : T.fgMuted, fontVariantNumeric: "tabular-nums" }}>{l.day} · {l.time}{l.label ? ` ${l.label}` : ""}</span>)}
    </span>
  );
};

// Who a step is for, from the client's side. Thread's steps say so quietly;
// "You" is outlined so the client sees at a glance when the ball is in
// their court. Steps without an owner (older engagements) show nothing.
const OWNER = { thread: ["Thread", "quiet"], client: ["You", "strong"], both: ["Together", "soft"] };
export const OwnerTag = ({ owner }) => {
  const o = OWNER[owner];
  if (!o) return null;
  const [text, weight] = o;
  const border = weight === "strong" ? T.borderStrong : weight === "soft" ? T.borderSubtle : "transparent";
  return <span data-owner={owner} style={{ display: "inline-block", fontSize: 11, fontWeight: 600, letterSpacing: "0.04em", lineHeight: 1.4, padding: "1px 8px", borderRadius: T.radiusPill, border: `1px solid ${border}`, color: weight === "quiet" ? T.fgMuted : weight === "strong" ? T.root : T.fgSecondary, whiteSpace: "nowrap", flex: "none" }}>{text}</span>;
};

const Row = ({ r, showPhase, zones }) => (
  <div className="step-row" style={{ display: "flex", alignItems: "baseline", gap: 14, padding: "11px 0", borderTop: `1px solid ${T.borderSubtle}` }}>
    <div style={{ flex: 1, minWidth: 0, fontSize: 15, color: r.status === "complete" ? T.fgMuted : T.root }}>
      <span style={{ fontWeight: 600 }}>{r.label}</span>
      {(r.description || showPhase) && <span style={{ fontSize: 13, color: T.fgMuted }}> · {r.description}{r.description && showPhase ? " · " : ""}{showPhase ? r.phase : ""}</span>}
    </div>
    <OwnerTag owner={r.owner} />
    {r.date && <When r={r} zones={zones} />}
    {r.status && r.status !== "to-start" && <Pill tone={(STEP_TONE[r.status] || STEP_TONE["to-start"])[0]}>{(STEP_TONE[r.status] || STEP_TONE["to-start"])[1]}</Pill>}
  </div>
);

// `steps` arrive already filtered for the client (clientSteps in ./now);
// `visiblePhases` is Activation and In Flight, plus Pre-engagement on a demo.
export default function Roadmap({ steps, today, cfg, finance, blockSessions, visiblePhases = CLIENT_PHASES }) {
  const zones = zonesFor(cfg);
  const [all, setAll] = useState(false);
  const todayIso = today || new Date().toLocaleDateString("en-CA");
  // Coaching sessions on the roadmap: a switch, on by default when Finance
  // Config says so (36 Degrees), off for large cohorts (Keynes).
  const [showSessions, setShowSessions] = useState(!!cfg?.roadmapSessions);
  const sessions = sessionDots(finance, blockSessions, todayIso);
  const sessionRows = showSessions && sessions.length > 0 ? [{ key: "Coaching", label: "Coaching", dots: sessions.map(d => ({ ...d, status: chartStatus(d.status) })) }] : [];
  const nextSessions = showSessions ? upcomingSessions(sessions, todayIso) : [];
  const phases = visiblePhases.filter(p => Array.isArray(steps?.[p]) && steps[p].length > 0);
  const slice = roadmapSlice(steps, 3, visiblePhases);
  const owned = phases.some(p => steps[p].some(r => r.owner));
  // The same pill as the statement's "More" buttons, under the list.
  const more = { background: "none", border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusPill, padding: "4px 14px", cursor: "pointer", fontFamily: T.fontSans, fontSize: 13, fontWeight: 600, color: T.fgSecondary };
  return (
    <section>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, marginBottom: 6 }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, color: T.root, margin: 0 }}>Your roadmap</h2>
        {sessions.length > 0 && <button onClick={() => setShowSessions(v => !v)} aria-pressed={showSessions} style={{ ...more, padding: "3px 12px", fontSize: 12, background: showSessions ? T.root : "none", color: showSessions ? T.fgOnDark : T.fgSecondary, borderColor: showSessions ? T.root : T.borderSubtle }}>Coaching sessions</button>}
      </div>
      {owned && <p style={{ fontSize: 13, color: T.fgSecondary, margin: "0 0 8px" }}>We run this side. Steps marked <OwnerTag owner="client" /> need someone on your side; for the rest, you just show up.</p>}
      {phases.length === 0 && <p style={{ fontSize: 15, color: T.fgSecondary, margin: 0 }}>Your roadmap will appear here once we have set the engagement up.</p>}
      {phases.length > 0 && <Timeline steps={steps} phases={visiblePhases} today={todayIso} extraRows={sessionRows} />}
      {!all && phases.length > 0 && (
        <>
          {slice.last && <p style={{ fontSize: 13, color: T.fgMuted, margin: "6px 0 0" }}>Last done: {slice.last.label}{slice.last.date ? `, ${weekdayDate(slice.last.date)}` : ""}</p>}
          {slice.next.length > 0 && <><div style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: T.fgMuted, margin: "18px 0 4px" }}>Next</div>{slice.next.map(r => <Row key={r.id} r={r} zones={zones} />)}</>}
          {nextSessions.length > 0 && <><div style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: T.fgMuted, margin: "18px 0 4px" }}>Coaching · next</div>{nextSessions.map(d => <Row key={d.id} r={{ id: d.id, label: d.label, date: d.date }} zones={zones} />)}</>}
        </>
      )}
      {all && phases.map(phase => {
        const rows = steps[phase];
        const done = rows.filter(r => r.status === "complete").length;
        return (
          <div key={phase} style={{ marginTop: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "10px 0 2px" }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: T.root }}>{phase}</span>
              <span style={{ fontSize: 13, color: done === rows.length ? T.statusOn : T.fgMuted }}>{done} of {rows.length} done</span>
            </div>
            {rows.map(r => <Row key={r.id} r={{ ...r, phase }} zones={zones} />)}
          </div>
        );
      })}
      {slice.hidden > 0 && <div style={{ padding: "12px 0 4px" }}><button onClick={() => setAll(a => !a)} aria-expanded={all} style={more}>{all ? "Show less ▴" : `Show all ${slice.total} ▾`}</button></div>}
    </section>
  );
}
